import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { AdminLoginRequest, AdminLoginResponse, AuthInfo } from '@/types/auth.types';
import { API_URL } from '@/lib/config';

export type APIResponse<TData = ''> = {
  success: boolean;
  data: TData;
  meta: {
    timestamp: Date;
    path: '/${string}';
  };
};

// Configure the base query with credentials to include cookies
const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  credentials: 'include', // Include cookies in requests
  prepareHeaders: (headers) => {
    // Set content type for JSON requests
    //headers.set('content-type', 'application/json');
    return headers;
  },
});

export const api = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: [
    'Auth',
    'Accounts',
    'Authenticator',
    'Dashboard',
    'Users',
    'InmopublicUsers',
    'InmopublicAccounts',
    'InmopublicMemberships',
  ],
  endpoints: (builder) => ({
    adminLogin: builder.mutation<AdminLoginResponse, AdminLoginRequest>({
      query: (credentials) => ({
        url: 'auth/admin-login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Auth'],
    }),

    getMe: builder.query<AuthInfo, void>({
      query: () => 'admin/me',
      transformResponse: (response: APIResponse<{ authInfo: AuthInfo }>) => response?.data?.authInfo,
      providesTags: ['Auth'],
    }),

    // api.ts
    logout: builder.mutation<void, void>({
      query: () => ({ url: 'admin/logout', method: 'POST' }),

      // Immediately wipe client-side state even if the request fails
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled; // wait for server ACK (optional)
        } finally {
          // 1) Remove *all* RTK Query cache so old data can't resurface
          dispatch(api.util.resetApiState());

          // 2) Cross-tab broadcast (optional but nice UX)
          localStorage.setItem('logout', Date.now().toString());
          localStorage.removeItem('logout'); // fire StorageEvent without litter
        }
      },
    }),
  }),
});

export const { useAdminLoginMutation, useGetMeQuery, useLogoutMutation } = api;
